import { computed, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'

export function useKbCategoryManagement(options) {
  const {
    loadCategoryTree,
    createCategory,
    updateCategory,
    defaultStatus = 0,
    includeRemark = false,
    messages
  } = options

  const categoryTree = ref([])
  const categoryLoading = ref(false)
  const selectedCategoryId = ref(null)
  const showCategoryModal = ref(false)
  const isCategoryEdit = ref(false)

  const createInitialForm = () => ({
    id: null,
    parentId: 0,
    name: '',
    sort: 0,
    status: defaultStatus,
    ...(includeRemark ? { remark: '' } : {})
  })

  const categoryForm = reactive(createInitialForm())

  const resetCategoryForm = (overrides = {}) => {
    Object.assign(categoryForm, createInitialForm(), overrides)
  }

  const loadCategories = async () => {
    categoryLoading.value = true
    try {
      categoryTree.value = await loadCategoryTree()
    } catch (error) {
      ElMessage.error(error.message || messages.loadError)
    } finally {
      categoryLoading.value = false
    }
  }

  const flatCategories = computed(() => {
    const result = []
    const flatten = (nodes, prefix = '') => {
      for (const node of nodes) {
        result.push({ id: node.id, label: prefix + node.name })
        if (node.children?.length) {
          flatten(node.children, prefix + node.name + ' / ')
        }
      }
    }
    flatten(categoryTree.value)
    return result
  })

  const getCategoryName = (id) => {
    if (!id) return '-'
    const match = flatCategories.value.find((item) => Number(item.id) === Number(id))
    return match ? match.label : '-'
  }

  const getLastCategoryName = (id) => {
    const label = getCategoryName(id)
    if (label === '-') return label
    const parts = label.split(' / ')
    return parts[parts.length - 1]
  }

  const handleCategorySelect = (id, callback) => {
    selectedCategoryId.value = selectedCategoryId.value === id ? null : id
    callback?.(selectedCategoryId.value)
  }

  const handleAddCategory = () => {
    resetCategoryForm()
    isCategoryEdit.value = false
    showCategoryModal.value = true
  }

  const handleAddChildCategory = (parentNode) => {
    resetCategoryForm({ parentId: parentNode.id })
    isCategoryEdit.value = false
    showCategoryModal.value = true
  }

  const handleEditCategory = (node) => {
    resetCategoryForm({
      id: node.id,
      parentId: node.parentId ?? 0,
      name: node.name,
      sort: node.sort ?? node.sortOrder ?? 0,
      status: node.status ?? defaultStatus,
      ...(includeRemark ? { remark: node.remark ?? '' } : {})
    })
    isCategoryEdit.value = true
    showCategoryModal.value = true
  }

  const closeCategoryModal = () => {
    showCategoryModal.value = false
  }

  const submitCategory = async () => {
    if (!categoryForm.name.trim()) {
      ElMessage.warning(messages.emptyNameWarning)
      return
    }

    const payload = {
      ...categoryForm,
      sort: categoryForm.sort ?? 0
    }

    try {
      if (isCategoryEdit.value) {
        await updateCategory(categoryForm.id, payload)
        ElMessage.success(messages.updateSuccess)
      } else {
        await createCategory(payload)
        ElMessage.success(messages.createSuccess)
      }
      closeCategoryModal()
      await loadCategories()
    } catch (error) {
      ElMessage.error(error.message || messages.saveError)
    }
  }

  return {
    categoryTree,
    categoryLoading,
    selectedCategoryId,
    showCategoryModal,
    isCategoryEdit,
    categoryForm,
    flatCategories,
    loadCategories,
    getCategoryName,
    getLastCategoryName,
    handleCategorySelect,
    handleAddCategory,
    handleAddChildCategory,
    handleEditCategory,
    closeCategoryModal,
    submitCategory
  }
}
